import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/client";
import { Customer } from "../types";
import { useAuth } from "../context/AuthContext";
import { usePublishScreenData } from "../utils/aiScreenContext";

export default function Customers() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    name: "",
    domain: "",
    primary_contact: "",
    contact_email: "",
    contact_phone: "",
    portal_can_view_invoices: true,
  });
  const [error, setError] = useState("");
  const canEdit = user?.role !== "viewer";

  useEffect(() => {
    api.get("/api/v1/customers").then((r) => setCustomers(r.data));
  }, []);

  const filtered = customers.filter(
    (c) =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      (c.domain || "").toLowerCase().includes(search.toLowerCase()),
  );

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    try {
      await api.post("/api/v1/customers", form);
      setShowForm(false);
      setForm({
        name: "",
        domain: "",
        primary_contact: "",
        contact_email: "",
        contact_phone: "",
        portal_can_view_invoices: true,
      });
      api.get("/api/v1/customers").then((r) => setCustomers(r.data));
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to create customer");
    }
  };

  usePublishScreenData(
    "Customers",
    filtered.map((c: any) => ({
      id: c.id,
      name: c.name,
      devices: c.device_count,
      open_tickets: c.open_ticket_count,
    })),
    search ? `search: ${search}` : undefined,
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Customers</h1>
        {canEdit && (
          <button
            onClick={() => setShowForm(true)}
            className="bg-blue-600 text-white text-sm px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Add Customer
          </button>
        )}
      </div>

      <input
        type="text"
        placeholder="Search customers..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="mb-4 border border-gray-300 dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100 rounded-lg px-3 py-2 text-sm w-72 focus:outline-none focus:ring-2 focus:ring-blue-500"
      />

      {showForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">New Customer</h2>
            <form onSubmit={submit} className="space-y-3">
              {[
                ["Company Name *", "name", true],
                ["Domain", "domain", false],
                ["Primary Contact", "primary_contact", false],
                ["Contact Email", "contact_email", false],
                ["Contact Phone", "contact_phone", false],
              ].map(([label, field, req]) => (
                <div key={field as string}>
                  <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">{label as string}</label>
                  <input
                    value={(form as any)[field as string]}
                    onChange={(e) => setForm({ ...form, [field as string]: e.target.value })}
                    required={req as boolean}
                    className="w-full border dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              ))}
              <label className="flex items-start gap-2 text-sm pt-1">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={form.portal_can_view_invoices}
                  onChange={(e) =>
                    setForm({ ...form, portal_can_view_invoices: e.target.checked })
                  }
                />
                <span>
                  <span className="font-medium">Default invoice access for new portal users</span>
                  <span className="block text-xs text-gray-500 dark:text-gray-400 font-normal">
                    Initial value of the Invoices flag when inviting a portal user for this
                    customer. Each user can be toggled individually later.
                  </span>
                </span>
              </label>
              {error && <p className="text-sm text-red-500">{error}</p>}
              <div className="flex gap-2 pt-2">
                <button
                  type="submit"
                  className="bg-blue-600 text-white text-sm px-4 py-2 rounded-lg"
                >
                  Create
                </button>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="border dark:border-gray-600 dark:text-gray-200 text-sm px-4 py-2 rounded-lg"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((c) => (
          <div
            key={c.id}
            onClick={() => navigate(`/customers/${c.id}`)}
            className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-5 cursor-pointer hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/40 rounded-lg flex items-center justify-center text-blue-600 dark:text-blue-300 font-bold text-lg">
                {c.name.charAt(0).toUpperCase()}
              </div>
              <span
                className={`text-xs px-2 py-0.5 rounded-full font-medium ${c.is_active ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300" : "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400"}`}
              >
                {c.is_active ? "Active" : "Inactive"}
              </span>
            </div>
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">{c.name}</h3>
            {c.domain && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{c.domain}</p>}
            {c.primary_contact && <p className="text-xs text-gray-500 dark:text-gray-400">{c.primary_contact}</p>}
            <div className="flex gap-4 mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
              <div className="text-center">
                <div className="text-lg font-bold text-gray-800 dark:text-gray-200">{c.device_count}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Devices</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-green-600 dark:text-green-400">{c.online_count}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Online</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-red-500 dark:text-red-400">
                  {c.device_count - c.online_count}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Offline</div>
              </div>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="col-span-3 text-center py-16">
            {search ? (
              <>
                <p className="text-gray-500 dark:text-gray-400">No customers match &ldquo;{search}&rdquo;.</p>
                <button
                  onClick={() => setSearch("")}
                  className="mt-2 text-sm text-blue-600 hover:underline"
                >
                  Clear search
                </button>
              </>
            ) : (
              <>
                <p className="text-gray-900 dark:text-gray-100 font-medium">No customers yet</p>
                <p className="text-gray-500 dark:text-gray-400 text-sm mt-1 mb-4">
                  Add your first customer to start onboarding their devices.
                </p>
                {canEdit && (
                  <div className="flex items-center justify-center gap-2">
                    <button
                      onClick={() => setShowForm(true)}
                      className="bg-blue-600 text-white text-sm px-4 py-2 rounded-lg hover:bg-blue-700"
                    >
                      Add customer
                    </button>
                    <button
                      onClick={() => navigate("/onboarding")}
                      className="border border-gray-300 dark:border-gray-600 dark:text-gray-200 text-sm px-4 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/40"
                    >
                      Onboard with installer
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
